import type { FontFamily, Manifest } from '@clawharbor/manifest';
import { contrastFailures, describeIssue } from '@clawharbor/render-core';
import { html, SP } from '../html.js';
import type { VNode } from '../html.js';
import { wordmarkText } from '../tenant-theme.js';

/**
 * The specimen.
 *
 * Everything inside `.tenant` is painted by `tokensToCss(brand, '.tenant')`, so the
 * swatches, the corners, the weights and the substituted face are the real
 * values the two product surfaces would ship — not a preview of them. The card
 * is the "wow", and it earns that by being literally the same code path.
 *
 * It deliberately shows the unflattering numbers too: a 0.55-confidence token
 * is labelled 0.55, and a palette that fails WCAG AA says which pair and by how
 * much. An operator approving a brand they cannot read is the failure mode this
 * screen exists to prevent.
 */

function FamilyRow(role: string, fam: FontFamily): VNode {
  const sub = fam.substitute;
  const substituted = sub && sub.family !== fam.observed;
  return html`
    <div class="typespec__row">
      <div
        class="typespec__sample"
        style=${role === 'display'
          ? 'font-family: var(--ch-font-display, var(--ch-font-sans)); font-weight: var(--ch-font-weight-heading, 700);'
          : 'font-family: var(--ch-font-sans); font-weight: var(--ch-font-weight-base, 400);'}
      >
        Grafički čvor — 0123
      </div>
      <div class="typespec__facts">
        <b>${role}</b> · observed ${fam.observed}<br />
        ${sub
          ? html`${substituted ? 'substituted' : 'served'} ${sub.family} · ${sub.license ?? sub.source}<br />
              ${sub.weights.join(' / ')} · ${sub.subsets.join(', ')}`
          : html`no substitute declared`}
      </div>
    </div>
  `;
}

export function BrandSpec(props: { manifest: Manifest }): VNode {
  const m = props.manifest;
  const brand = m.brand;
  const entries = Object.entries(brand.palette).filter(([, t]) => t != null);
  const failures = contrastFailures(brand);
  const sans = brand.typography.families.sans;
  const display = brand.typography.families.display;
  const unlicensed = [sans, display].filter((f): f is FontFamily => f != null && !f.licensed);

  return html`
    <div class="spec tenant">
      <div class="spec__top">
        <h3 class="spec__wordmark">${wordmarkText(m)}</h3>
        <span class="spec__url">${m.source.canonicalOrigin} · ${brand.mode} · ${entries.length} tokens</span>
      </div>

      <p class="spec__label">Palette</p>
      <div class="swatches">
        ${entries.map(
          ([role, token]) => html`
            <div class="swatch">
              <div class="swatch__chip" style=${`background:${token?.value ?? 'transparent'}`}></div>
              <div class="swatch__meta">
                <span class="swatch__role">${role}</span>
                <span class="swatch__hex">${token?.value}</span>
                <span class="swatch__conf">
                  conf ${(token?.confidence ?? 0).toFixed(2)}${token?.derived ? ' · derived' : ''}
                </span>
              </div>
            </div>
          `,
        )}
      </div>

      <p class="spec__label">Type</p>
      <div class="typespec">
        ${FamilyRow('sans', sans)} ${display ? FamilyRow('display', display) : null}
      </div>

      <p class="spec__label">
        Shape · radius ${Object.entries(brand.shape.radius).map(([k, v]) => `${k} ${v}px`).join(' · ')}
      </p>
      <div class="shapes">
        <div class="shapes__demo shapes__demo--card">Card</div>
        <div class="shapes__demo shapes__demo--control">Primary</div>
        <div class="shapes__demo shapes__demo--chip">Chip</div>
      </div>

      ${unlicensed.length
        ? html`<p class="note">
            Not rehosted: ${unlicensed.map((f) => f.observed).join(', ')}.${SP}
            ${unlicensed[0]?.licenseNote ?? ''}
          </p>`
        : null}
      ${failures.length
        ? html`<p class="note note--warn">
            ${failures.length} contrast pair${failures.length === 1 ? '' : 's'} below WCAG AA —${SP}
            ${describeIssue(failures[0] as (typeof failures)[number])}.${SP}
            The surfaces correct these at render time rather than shipping unreadable text.
          </p>`
        : html`<p class="note">Every pair the stylesheet uses meets WCAG AA as extracted.</p>`}
    </div>
  `;
}
